import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { FluentProvider } from '@fluentui/react-components';
import { themeCssVars, themeForMode, type ThemeMode } from './ptcThemes';

/**
 * Theme mode resolution: OS `prefers-color-scheme` is the default; an explicit
 * user choice (the header sun/moon toggle) is persisted to localStorage under
 * `sup:theme` and overrides the OS from then on. While no explicit choice is
 * stored, the app live-follows OS theme changes.
 */
export const THEME_STORAGE_KEY = 'sup:theme';

function readStoredMode(): ThemeMode | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    return null; // storage unavailable (private mode / blocked) — fall back to OS
  }
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Exported for tests: stored override wins; otherwise the OS preference. */
export function resolveInitialMode(): ThemeMode {
  return readStoredMode() ?? (systemPrefersDark() ? 'dark' : 'light');
}

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(resolveInitialMode);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // persistence is best-effort; the in-memory mode still applies
    }
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  // Live-follow OS theme changes until the user makes an explicit choice.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    if (typeof mql.addEventListener !== 'function') return;
    const onChange = (event: MediaQueryListEvent) => {
      if (readStoredMode() !== null) return; // explicit override in place
      setModeState(event.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Project the active theme onto :root — plain-CSS surfaces (index.css, the
  // shell chrome) read these variables; the attribute keys the dark-only CSS
  // (popover elevation step) and index.html's pre-mount FOUC guard sets the
  // same attribute before React loads.
  useEffect(() => {
    const root = document.documentElement;
    for (const [name, value] of Object.entries(themeCssVars[mode])) {
      root.style.setProperty(name, value);
    }
    root.setAttribute('data-sup-theme', mode);
    root.style.colorScheme = mode; // native scrollbars, form controls, etc.
  }, [mode]);

  const value = useMemo(
    () => ({ mode, setMode, toggleMode }),
    [mode, setMode, toggleMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return ctx;
}

/** FluentProvider bound to the active PTC theme (light/dark). */
export function ThemedFluentProvider({ children }: { children: ReactNode }) {
  const { mode } = useThemeMode();
  return <FluentProvider theme={themeForMode(mode)}>{children}</FluentProvider>;
}
